import { useMemo } from "react"
import { usePermissions } from "@/hooks/use-permissions"
import { useAuthStore } from "@/stores/auth-store"
import { hasFlag } from "@/lib/feature-flags"
import { REPORTS, type ReportDomain, type ReportEntry, type ReportSlug } from "@/lib/reports.generated"
import type { FeatureFlags, ReportPermissionGrants } from "@/types"

/**
 * Report access, resolved from the generated registry (config/reports.php via
 * `php artisan reports:sync`). The sidebar, <ReportGuard> and any "is this
 * report open to me" question go through here, so the three cannot disagree.
 *
 * Permission model: `reports.<domain>` opens every report in the domain
 * clinic-wide; `reports.<domain>.own` opens only the reports whose entry says
 * `own: true`, narrowed to the caller server-side (ReportScope).
 *
 * A clinic may WIDEN one report to an existing permission its domain key does
 * not reach (config/reports.php `extra_permissions`, ArtDent 260917). That map
 * is per-clinic, so it rides the auth payload beside feature_flags rather than
 * the generated registry, which describes the whole fleet. It only ever adds:
 * the domain key opens the report everywhere, exactly as before.
 */

export type ReportScope = "clinic" | "own"

export interface ReportAccess {
  /** the registry entry */
  entry: ReportEntry
  /** every required flag is on for this clinic */
  runs: boolean
  /** a presentation gate says to hide it in this clinic mode */
  hidden: boolean
  /** the caller holds a key that opens it (null = no) */
  scope: ReportScope | null
  /** runs && !hidden && scope !== null */
  allowed: boolean
}

export function domainPermission(domain: ReportDomain): string {
  return `reports.${domain}`
}

export function domainOwnPermission(domain: ReportDomain): string {
  return `reports.${domain}.own`
}

export function reportEntry(slug: ReportSlug): ReportEntry {
  return REPORTS[slug]
}

/**
 * Pure resolver — the hook below wraps it. `permissions` is the user's flat
 * Spatie list; `flags` the clinic's feature_flags payload.
 */
export function resolveReportAccess(
  slug: ReportSlug,
  permissions: readonly string[] | undefined | null,
  flags: FeatureFlags | undefined | null,
  grants?: ReportPermissionGrants | null
): ReportAccess {
  const entry = REPORTS[slug]
  const held = new Set(permissions ?? [])
  const runs = entry.flags.every((flag) => hasFlag(flags, flag as keyof FeatureFlags))
  const hidden =
    entry.hideWhen.some((flag) => hasFlag(flags, flag as keyof FeatureFlags)) ||
    // Opt-OUT (default-true) flags: hide only on an explicit false, so a
    // payload from an older backend leaves the report visible.
    entry.hideWhenOff.some((flag) => flags?.[flag as keyof FeatureFlags] === false)
  // Clinic-wide, like the domain key: a widened permission opens the whole
  // report, not a slice of it. The server-side gate (ReportGate) ORs the same
  // list, so the two cannot disagree.
  const widened = (grants?.[slug] ?? []).some((permission) => held.has(permission))
  const scope: ReportScope | null =
    held.has(domainPermission(entry.domain)) || widened
      ? "clinic"
      : entry.own && held.has(domainOwnPermission(entry.domain))
        ? "own"
        : null

  return { entry, runs, hidden, scope, allowed: runs && !hidden && scope !== null }
}

/**
 * May the caller act on a report's WRITE action — today, settling a hotel bill?
 *
 * Mirrors ReportGate::settle exactly: the `admin` role always may, and a clinic
 * may widen the action to existing permissions (`report_settle_permissions` on
 * the auth payload). Read access is deliberately NOT consulted — seeing what
 * the clinic owes its hotels is not permission to declare a bill paid.
 */
export function resolveReportSettle(
  slug: ReportSlug,
  roles: readonly string[] | undefined | null,
  permissions: readonly string[] | undefined | null,
  grants?: ReportPermissionGrants | null
): boolean {
  if ((roles ?? []).includes("admin")) return true

  const held = new Set(permissions ?? [])

  return (grants?.[slug] ?? []).some((permission) => held.has(permission))
}

/** Hook form of resolveReportSettle; false until /me has resolved. */
export function useReportSettle(slug: ReportSlug): boolean {
  const roles = useAuthStore((s) => s.user?.roles)
  const permissions = useAuthStore((s) => s.user?.permissions)
  const grants = useAuthStore((s) => s.user?.report_settle_permissions)

  return useMemo(
    () => resolveReportSettle(slug, roles, permissions, grants),
    [slug, roles, permissions, grants]
  )
}

/**
 * Hook form. `isLoaded` is false until /me resolved; every verdict is
 * "not allowed" until then (the usePermissions hide-during-hydration
 * contract), so gate UI on `isLoaded` before treating a denial as final.
 */
export function useReportAccess(slug: ReportSlug): ReportAccess & { isLoaded: boolean } {
  const { isLoaded, permissions } = usePermissions()
  const flags = useAuthStore((s) => s.user?.feature_flags)
  const grants = useAuthStore((s) => s.user?.report_permissions)

  return useMemo(
    () => ({ ...resolveReportAccess(slug, permissions, flags, grants), isLoaded }),
    [slug, permissions, flags, grants, isLoaded]
  )
}
